// @ts-strict-ignore
/** @jsx jsx */
/** @jsxFrag null */
import {jsx} from 'panel';

import {ModalEl, ModalType} from 'common/components/modal';
import {IconSizeType} from 'common/constants';
import {SvgIconEl} from 'common/components/svg-icon';
import {TEXT_HIGHLIGHT_THEME, TextHighlightEl} from 'common/components/text-highlight';
import {RevealableTextEditEl} from 'common/components/revealable-text-editor';
import {ToggleEl} from 'common/components/toggle';
import {ToggleSize, ToggleTheme} from 'common/components/toggle/types';
import {TooltipEl} from 'common/components/tooltip';
import {userDetailsStore} from 'common/report/stores/user-details';
import {capitalize} from 'common/util/string';
// eslint-disable-next-line import/no-cycle
import {AvatarUploadEl} from 'common/widgets/avatar-upload';
// oxlint-disable-next-line no-cycle
import {UserIconEl} from 'common/widgets/user-icon';
import {ButtonEl} from 'common/components/button';
import {ButtonTheme} from 'common/components/button/constants';

import type {TeammateSidebar} from './index';
import {SidebarEntity, TabOptions} from './types';

function getReportRow({type, name, link}, trackFn) {
  // TODO use the entity-management components
  let icon = `brand-${type}`;
  if (type === `board`) {
    icon = `brand-dashboard`;
  }

  return (
    <a sel='.entry.report-entry.entry-title' attrs={{href: link}} on={{click: () => trackFn({type, name, link})}}>
      <SvgIconEl attrs={{icon}} />
      <>{name}</>
    </a>
  );
}

function getTeamRow({name}) {
  return (
    <div sel='.entry'>
      <div sel='.entry-title.entry-content'>{name}</div>
    </div>
  );
}

function LoadingSection() {
  return (
    <div sel='.loading-section'>
      <div sel='.mp-skeleton-large' />
      <div sel='.mp-skeleton-large' />
      <div sel='.mp-skeleton-large' />
      <div sel='.mp-skeleton-large' />
    </div>
  );
}

function getReportContent(reports: SidebarEntity[], trackFn) {
  if (!reports) {
    return LoadingSection();
  } else if (reports?.length) {
    return reports.filter((report) => !report.isHidden)?.map((report) => getReportRow(report, trackFn));
  }
  return (
    <div sel='.entry'>
      <div sel='.entry-content'>No recent reports or boards shared with you</div>
    </div>
  );
}

function getTeamsContent(user) {
  if (!user) {
    return LoadingSection();
  } else if (user.teams?.length) {
    return user.teams.map((team) => getTeamRow(team));
  }
  return (
    <div sel='.entry'>
      <>Not a member of any teams within this organization</>
    </div>
  );
}

function getTableSection(content) {
  return (
    <div sel='.info-container'>
      <div sel='.table-section'>{content}</div>
    </div>
  );
}

export function template(this: TeammateSidebar) {
  const {selectedTab} = this.state;
  const bio = this.helpers.userIsMe() ? userDetailsStore.value?.bio : this.state.user?.bio;
  return (
    <div sel='.teammate-sidebar'>
      <ModalEl
        attrs={{
          'modal-type': ModalType.RightDrawer,
          open: true,
          closeable: true,
          'hide-backdrop': true,
        }}
        on={{change: this.helpers.handleModalChange}}
      >
        <div sel='.content-container'>
          <div sel='.top-section'>
            {this.helpers.userIsMe() ? (
              <AvatarUploadEl
                params={{
                  id: this.attr(`id`),
                  hideName: true,
                }}
                on={{
                  avatarChanged: this.helpers.handleAvatarChanged,
                }}
              />
            ) : (
              <UserIconEl
                sel='.icon'
                attrs={{
                  'disable-sidebar': true,
                  name: this.attr(`name`),
                  id: this.attr(`id`),
                  email: this.attr(`email`),
                  size: IconSizeType.XL,
                }}
              />
            )}
            <div sel='.name'>{this.attr(`name`)}</div>
            <div sel='.email'>{this.attr(`email`)}</div>
            {this.state.user ? (
              this.state.user.role && this.state.user.active ? (
                <TextHighlightEl
                  sel='.role'
                  attrs={{
                    theme: TEXT_HIGHLIGHT_THEME.INFORMATION,
                    text: `Project ${capitalize(this.state.user?.role)}`,
                  }}
                />
              ) : (
                <span>
                  <TextHighlightEl sel='.role' attrs={{theme: TEXT_HIGHLIGHT_THEME.LIGHT, text: `Inactive`}} />
                  <TooltipEl attrs={{text: `User is no longer a member of the project`}} />
                </span>
              )
            ) : null}
            {this.helpers.userIsMe() ? (
              <div sel='.bio'>
                <RevealableTextEditEl attrs={{text: bio ?? ``}} on={{changedText: this.helpers.handleTextEditorSave}}>
                  <div sel='.bio-content' attrs={{slot: `content`}} class={{'existing-bio': !!bio}}>
                    <div sel='.text'>{bio || `Add a bio`}</div>
                    <div sel='.btn-area'>
                      <ButtonEl sel='.edit-bio-btn' attrs={{icon: `edit`, theme: ButtonTheme.Tertiary}} />
                      {bio && (
                        <ButtonEl
                          sel='.clear-bio-btn'
                          attrs={{icon: `circle-clear`, theme: ButtonTheme.Tertiary}}
                          on={{click: this.helpers.clearBio}}
                        />
                      )}
                    </div>
                  </div>
                </RevealableTextEditEl>
              </div>
            ) : bio ? (
              <div sel='.bio'>
                <div sel='.text'>{bio}</div>
              </div>
            ) : null}
            <ToggleEl
              sel='.tab-toggle'
              attrs={{
                theme: ToggleTheme.TabBar,
                selected: selectedTab,
                size: ToggleSize.Medium,
                options: [
                  {label: TabOptions.MostViewed, value: TabOptions.MostViewed},
                  {label: TabOptions.RecentlyCreated, value: TabOptions.RecentlyCreated},
                ],
              }}
              on={{select: (ev: CustomEvent) => this.helpers.handleTabSelect(ev)}}
            />
          </div>
          <div sel='.bottom-section'>
            {selectedTab === TabOptions.RecentlyCreated
              ? getTableSection(getReportContent(this.state.user?.recently_created, this.helpers.trackRecentlyCreated))
              : selectedTab === TabOptions.MostViewed
                ? getTableSection(getReportContent(this.state.mostViewed, this.helpers.trackMostViewed))
                : selectedTab === TabOptions.Teams
                  ? getTableSection(getTeamsContent(this.state.user))
                  : null}
          </div>
        </div>
      </ModalEl>
    </div>
  );
}
